import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import { rangeFromManual, type ManualEntryValues } from './hoursMath';

type TimeEntryRow = Database['public']['Tables']['time_entries']['Row'];

export interface TimeEntry {
  id: string;
  project_id: string | null;
  task_id: string | null;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number | null;
  description: string | null;
  billable: boolean;
  hourly_rate: number | null;
}

export interface TimerInput {
  project_id: string | null;
  description: string | null;
  billable: boolean;
}

export const timeEntriesKey = (orgId: string | null) => ['time-entries', orgId] as const;
export const activeTimerKey = (orgId: string | null, userId: string | null) =>
  ['active-timer', orgId, userId] as const;

function castRow(r: TimeEntryRow): TimeEntry {
  return {
    id: r.id,
    project_id: r.project_id,
    task_id: r.task_id,
    started_at: r.started_at,
    ended_at: r.ended_at,
    duration_minutes: r.duration_minutes,
    description: r.description,
    billable: r.billable,
    hourly_rate: r.hourly_rate,
  };
}

async function fetchEntries(orgId: string, sinceIso: string): Promise<TimeEntry[]> {
  const { data, error } = await supabase
    .from('time_entries')
    .select('id, project_id, task_id, started_at, ended_at, duration_minutes, description, billable, hourly_rate, organization_id, user_id, focus_task_id, rate_currency, created_at, updated_at, deleted_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .gte('started_at', sinceIso)
    .order('started_at', { ascending: false });
  if (error) throw error;
  return data.map(castRow);
}

async function fetchActiveTimer(orgId: string, userId: string): Promise<TimeEntry | null> {
  const { data, error } = await supabase
    .from('time_entries')
    .select('id, project_id, task_id, started_at, ended_at, duration_minutes, description, billable, hourly_rate, organization_id, user_id, focus_task_id, rate_currency, created_at, updated_at, deleted_at')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .is('ended_at', null)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return data === null ? null : castRow(data);
}

const ENTRY_COLUMNS =
  'id, project_id, task_id, started_at, ended_at, duration_minutes, description, billable, hourly_rate, organization_id, user_id, focus_task_id, rate_currency, created_at, updated_at, deleted_at';

async function fetchProjectEntries(orgId: string, projectId: string): Promise<TimeEntry[]> {
  const { data, error } = await supabase
    .from('time_entries')
    .select(ENTRY_COLUMNS)
    .eq('organization_id', orgId)
    .eq('project_id', projectId)
    .is('deleted_at', null)
    .order('started_at', { ascending: false });
  if (error) throw error;
  return data.map(castRow);
}

/** Every entry on one project, all time. Under timeEntriesKey so timer mutations refresh it. */
export function useProjectTimeEntries(orgId: string | null, projectId: string | null) {
  return useQuery({
    queryKey: [...timeEntriesKey(orgId), 'project', projectId] as const,
    queryFn: () =>
      orgId && projectId ? fetchProjectEntries(orgId, projectId) : Promise.resolve<TimeEntry[]>([]),
    enabled: Boolean(orgId && projectId),
    staleTime: 30 * 1000,
  });
}

/** Logged minutes per project, for rollups across many projects (a customer's). */
export function useProjectsMinutes(orgId: string | null, projectIds: string[]) {
  const sorted = [...projectIds].sort();
  return useQuery({
    queryKey: [...timeEntriesKey(orgId), 'minutes', sorted.join(',')] as const,
    queryFn: async () => {
      const totals: Record<string, number> = {};
      if (!orgId || sorted.length === 0) return totals;
      const { data, error } = await supabase
        .from('time_entries')
        .select('project_id, duration_minutes')
        .eq('organization_id', orgId)
        .in('project_id', sorted)
        .is('deleted_at', null)
        .not('duration_minutes', 'is', null);
      if (error) throw error;
      for (const row of data) {
        if (!row.project_id) continue;
        totals[row.project_id] = (totals[row.project_id] ?? 0) + row.duration_minutes;
      }
      return totals;
    },
    enabled: Boolean(orgId && sorted.length > 0),
    staleTime: 30 * 1000,
  });
}

export interface ManualTimeEntryInput extends ManualEntryValues {
  project_id: string | null;
  description: string | null;
  billable: boolean;
}

/** Hours typed in after the fact, or a correction to an existing entry. */
export function useUpsertTimeEntry(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: ManualTimeEntryInput }) => {
      if (!orgId || !userId) throw new Error('Missing context');
      const trimmed = values.description?.trim();
      const fields = {
        project_id: values.project_id,
        description: trimmed && trimmed.length > 0 ? trimmed : null,
        billable: values.billable,
        ...rangeFromManual(values),
      };
      const { error } = id
        ? await supabase.from('time_entries').update(fields).eq('id', id).eq('organization_id', orgId)
        : await supabase
            .from('time_entries')
            .insert({ ...fields, organization_id: orgId, user_id: userId });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: timeEntriesKey(orgId) });
      await qc.invalidateQueries({ queryKey: activeTimerKey(orgId, userId) });
    },
  });
}

export function useTimeEntries(orgId: string | null, sinceIso: string) {
  return useQuery({
    queryKey: [...timeEntriesKey(orgId), sinceIso] as const,
    queryFn: () => (orgId ? fetchEntries(orgId, sinceIso) : Promise.resolve<TimeEntry[]>([])),
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

export function useActiveTimer(orgId: string | null, userId: string | null) {
  return useQuery({
    queryKey: activeTimerKey(orgId, userId),
    queryFn: () =>
      orgId && userId ? fetchActiveTimer(orgId, userId) : Promise.resolve<TimeEntry | null>(null),
    enabled: Boolean(orgId && userId),
    staleTime: 5 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useStartTimer(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: TimerInput) => {
      if (!orgId || !userId) throw new Error('Missing context');
      const trimmed = values.description?.trim();
      const { error } = await supabase.from('time_entries').insert({
        organization_id: orgId,
        user_id: userId,
        project_id: values.project_id,
        description: trimmed && trimmed.length > 0 ? trimmed : null,
        billable: values.billable,
        started_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: activeTimerKey(orgId, userId) });
      await qc.invalidateQueries({ queryKey: timeEntriesKey(orgId) });
    },
  });
}

export function useStopTimer(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('time_entries')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: activeTimerKey(orgId, userId) });
      await qc.invalidateQueries({ queryKey: timeEntriesKey(orgId) });
    },
  });
}

export function useDeleteTimeEntry(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('time_entries')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: timeEntriesKey(orgId) });
    },
  });
}
